/** Read select-modality PAS + routing fields from asset.metadata.modalityAssessment. */

export type SelectModalityAssessmentDisplay = {
  pipeline?: string;
  modelUsed?: string;
  endpointTier?: 'primary' | 'backup';
  service?: string;
  assessmentSource?: string;
  chipLabel: string;
  detailLines: string[];
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function readSelectModalityAssessmentDisplay(
  metadata: unknown,
): SelectModalityAssessmentDisplay | null {
  const root = asRecord(metadata);
  const raw = asRecord(root?.modalityAssessment);
  if (!raw) return null;

  const modelUsed = String(raw.model_used || raw.modelUsed || '').trim() || undefined;
  const pipeline = String(raw.pipeline || '').trim() || undefined;
  const assessmentSource = String(raw.assessment_source || raw.assessmentSource || '').trim() || undefined;
  const service = String(raw.select_modality_service || raw.selectModalityService || '').trim() || undefined;
  const tierRaw = String(raw.endpoint_tier || raw.endpointTier || '').trim().toLowerCase();
  const endpointTier = tierRaw === 'backup' || tierRaw === 'primary'
    ? tierRaw as 'primary' | 'backup'
    : (service?.includes('backup') ? 'backup' as const : undefined);

  if (!modelUsed && !pipeline && !endpointTier && !service) {
    return null;
  }

  const tierLabel = endpointTier === 'backup'
    ? 'backup'
    : endpointTier === 'primary'
      ? 'primary'
      : (service?.includes('backup') ? 'backup' : 'select-modality');
  const chipLabel = modelUsed
    ? `模态·${tierLabel}·${modelUsed}`
    : `模态·${tierLabel}`;

  const detailLines = [
    service ? `服务：${service}` : null,
    endpointTier ? `端点：${endpointTier === 'backup' ? 'backup（热备）' : 'primary（主站）'}` : null,
    modelUsed ? `模型：${modelUsed}` : null,
    pipeline ? `链路：${pipeline}` : null,
    assessmentSource ? `来源：${assessmentSource}` : null,
  ].filter((line): line is string => Boolean(line));

  return {
    pipeline,
    modelUsed,
    endpointTier,
    service,
    assessmentSource,
    chipLabel,
    detailLines,
  };
}

export function isPasAnswerSheetSuspectedAsset(asset: {
  type?: string | null;
  metadata?: unknown;
} | null | undefined): boolean {
  if (!asset) return false;
  if (String(asset.type || '').trim().toLowerCase() === 'pas') return false;
  const metadata = asset.metadata;
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return false;
  const assessment = (metadata as Record<string, unknown>).modalityAssessment;
  if (!assessment || typeof assessment !== 'object' || Array.isArray(assessment)) return false;
  const record = assessment as Record<string, unknown>;
  return record.looks_like_pas_answer_sheet === true || record.looksLikePasAnswerSheet === true;
}
